import { FormikProps } from "formik";
import Modal, { ModalContext, ModalTrigger } from "../../../../../Modal/Modal";
import Button from "@/components/bricks/button/Button";
import EditIcon from "../../../../../icons/EditIcon";
import { useContext, useState } from "react";
import { StickyFooter } from "../../../../../stickyFooter/modalStickyFooter";
import { FirmaFields } from "./StamoplysningerTop";
import { IProduktionsenhed } from "../../../../../../interfaces/firma";
import { useQuery } from "@tanstack/react-query";
import IsRequired from "../../../../../formcontrols/common/IsRequired";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../../../../select/Select";
import {
  TicketHeading,
  TicketSub,
  TicketSubItem,
} from "../../../../../ticket/Ticket";
import DuplicateFirmaWarning from "../../../../../wizards/OpretFirma/steps/DuplicateFirmaWarning";

import { AlertMessage } from "../../../../../AlertMessage";
import { useDuplicateFirmaByPnrFinder } from "../../../../../../hooks/useDuplicateFirmaFinder";
import { FirmaByPnrResponse } from "../../../../../wizards/OpretFirma/opretFirmaInterface";
import { getProduktionsenhederForCvrNummer } from "@/api/cvr";

interface IFirmaStamPnrModalProps {
  cvrNr: number;
  formik: FormikProps<FirmaFields>;
}
function ModalContent(props: {
  formik: FormikProps<FirmaFields>;
  produktionsEnheder: IProduktionsenhed[];
}) {
  const { formik, produktionsEnheder } = props;
  const [produktionsEnhed, setProduktionsEnhed] = useState<
    IProduktionsenhed | undefined
  >(produktionsEnheder[0]);

  const { existingFirmaByPnr } = useDuplicateFirmaByPnrFinder(
    produktionsEnhed?.pnummer,
  );

  const { setModalOpen } = useContext(ModalContext);
  const { handleSubmit } = formik;
  return (
    <Modal
      title={
        <>
          <EditIcon /> Føj til p-nummer
        </>
      }
      description=""
    >
      <div className="c-modal-container">
        <form autoComplete="off" onSubmit={handleSubmit}>
          <div className="c-item-list c-modal-container-inner">
            <div className="flex-100">
              <label className="form-label">
                Vælg produktionsenhed <IsRequired />
              </label>
              {produktionsEnheder && produktionsEnheder.length > 0 && (
                <Select
                  disabled={produktionsEnheder.length === 0}
                  value={produktionsEnhed?.pnummer.toString()}
                  onValueChange={(value) => {
                    const produktionsEnhed = produktionsEnheder.find(
                      (pe) => pe.pnummer.toString() === value,
                    );
                    if (produktionsEnhed) setProduktionsEnhed(produktionsEnhed);
                  }}
                >
                  <SelectTrigger
                    aria-label="Produktionsenhed"
                    placeholder="Vælg..."
                  />
                  <SelectContent scrollable>
                    {produktionsEnheder &&
                      produktionsEnheder.map((pe) => {
                        return (
                          <SelectItem
                            value={pe.pnummer.toString()}
                            key={pe.pnummer.toString()}
                            className="py-2"
                            style={{ height: "unset" }}
                          >
                            <TicketHeading>
                              <h4
                                className="h5"
                                style={{ marginBottom: "0.15rem" }}
                              >
                                {pe.navn}
                              </h4>
                            </TicketHeading>

                            <TicketSub>
                              <TicketSubItem>{pe.adresse}</TicketSubItem>
                            </TicketSub>
                          </SelectItem>
                        );
                      })}
                  </SelectContent>
                </Select>
              )}
            </div>
            {existingFirmaByPnr && (
              <DuplicateFirmaWarning
                existingFirma={existingFirmaByPnr as FirmaByPnrResponse}
                onCancel={() => {
                  setModalOpen(false);
                }}
                reason={"p-nummer"}
              />
            )}
          </div>
          <StickyFooter>
            {existingFirmaByPnr ? (
              <span>&nbsp;</span>
            ) : (
              <>
                <Button
                  type="button"
                  color="secondary"
                  onClick={() => setModalOpen(false)}
                >
                  Annuller
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  onClick={() => {
                    formik.setFieldValue(
                      "pnr",
                      produktionsEnhed?.pnummer,
                      true,
                    );
                    setModalOpen(false);
                  }}
                  disabled={formik.isSubmitting}
                >
                  Føj til
                </Button>
              </>
            )}
          </StickyFooter>
        </form>
      </div>
    </Modal>
  );
}

function NoResultContent() {
  const { setModalOpen } = useContext(ModalContext);
  return (
    <Modal
      title={
        <>
          <EditIcon /> Føj til p-nummer
        </>
      }
      description=""
    >
      <div className="c-modal-container">
        <div className="c-item-list c-modal-container-inner">
          <div className="flex-100">
            <label className="form-label">
              Vælg produktionsenhed <IsRequired />
            </label>
            <AlertMessage
              className="mt-3"
              warningIcon
              type="warning"
              heading=" Der er ingen aktive produktionsenheder tilknyttet dette
                CVR-nummer."
            >
              <Button
                type="button"
                color="warning"
                onClick={() => setModalOpen(false)}
              >
                Annuller
              </Button>
            </AlertMessage>
          </div>
        </div>
        <StickyFooter>&nbsp;</StickyFooter>
      </div>
    </Modal>
  );
}
const FirmaStamPnrModal = (props: IFirmaStamPnrModalProps) => {
  const { modalOpen } = useContext(ModalContext);
  const { isLoading, data: produktionsEnheder } = useQuery<IProduktionsenhed[]>(
    {
      queryKey: ["getProduktionsEnhederForCvrNummer", props.cvrNr],
      queryFn: async () => await getProduktionsenhederForCvrNummer(props.cvrNr),
      staleTime: Infinity,
    },
  );
  return (
    <>
      <ModalTrigger asChild>
        <Button
          disabled={isLoading}
          type="button"
          className="btn--action w-100 fw-medium text-dark py-2 justify-content-between"
          color="transparent"
          onClick={() => {}}
        >
          {props.formik.values.pnr ? (
            <span>{props.formik.values.pnr}</span>
          ) : (
            <span>Føj til p-nr.</span>
          )}
          <span className="text-primary">
            <EditIcon />
          </span>
        </Button>
      </ModalTrigger>
      {produktionsEnheder && produktionsEnheder.length > 0 ? (
        <ModalContent
          key={modalOpen.toString()}
          produktionsEnheder={produktionsEnheder}
          formik={props.formik}
        />
      ) : (
        <NoResultContent />
      )}
    </>
  );
};

export default FirmaStamPnrModal;
